import {
    Component,
    Input,
    OnDestroy,
    OnInit,
    QueryList,
    ViewChild,
    ViewChildren,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { ActionButtonComponent } from "@components/action-button/action-button.component";
import { ButtonComponent } from "@components/button/button.component";
import { BlockComponent } from "@components/container-block/block.component";
import { HeaderComponent } from "@components/header/header.component";
import { TabComponent } from "@components/tabs/tab/tab.component";
import { TabsComponent as TabsComponent_1 , TabsComponent } from "@components/tabs/tabs.component";
import { cloneDeep } from "lodash";
import { Subject, Subscription } from "rxjs";
import { take, takeUntil } from "rxjs/operators";
import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { ITab } from "~app/common/components/tabs/tab/itab";

import { ConfirmDialogResponse } from "~app/common/models/constants";
import { IGetArbetsorderRequest } from "~app/features/planera/model/IGetArbetsorderRequest";
import { IPlaneraBasdataResponse } from "~app/features/planera/model/IPlaneraBasdataResponse";
import { ArbetsorderSearchHybridService } from "./arbetsorder-search-hybrid.service";
import { ArbetsorderSearchMinaFilterService } from "./arbetsorder-search-mina-filter.service";
import { ArbetsorderSparaSokfilterComponent } from "./components/arbetsorder-spara-sokfilter/arbetsorder-spara-sokfilter.component";
import { ArbetsorderSearchArbetsmomentComponent } from "./filters/arbetsorder-search-arbetsmoment/arbetsorder-search-arbetsmoment.component";
import { ArbetsorderSearchGrotComponent } from "./filters/arbetsorder-search-grot/arbetsorder-search-grot.component";
import { ArbetsorderSearchHeaderFilterComponent } from "./filters/arbetsorder-search-header-filter/arbetsorder-search-header-filter.component";
import { ArbetsorderSearchMatningComponent } from "./filters/arbetsorder-search-matning/arbetsorder-search-matning.component";
import { ArbetsorderSearchSkogligDataComponent } from "./filters/arbetsorder-search-skoglig-data/arbetsorder-search-skoglig-data.component";
import { ArbetsorderSearchStatusComponent } from "./filters/arbetsorder-search-status/arbetsorder-search-status.component";
import { ArbetsorderSearchTidComponent } from "./filters/arbetsorder-search-tid/arbetsorder-search-tid.component";
import { ArbetsorderSearchVirkeskopComponent } from "./filters/arbetsorder-search-virkeskop/arbetsorder-search-virkeskop.component";
import { ArbetsorderSearchBasePanel } from "./models/arbetsorder-search-base-panel";
import { ArbetsorderSearchTexts } from "./models/arbetsorder-search-texts";
import { INamedSokfilter } from "./models/named-sokfilter";

@Component({
    selector: "rask-arbetsorder-search",
    templateUrl: "./arbetsorder-search.component.html",
    styleUrls: ["./arbetsorder-search.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        BlockComponent,
        HeaderComponent,
        ActionButtonComponent,
        ArbetsorderSparaSokfilterComponent,
        TabsComponent_1,
        TabComponent,
        ArbetsorderSearchHeaderFilterComponent,
        ArbetsorderSearchSkogligDataComponent,
        ArbetsorderSearchArbetsmomentComponent,
        ArbetsorderSearchStatusComponent,
        ArbetsorderSearchTidComponent,
        ArbetsorderSearchGrotComponent,
        ArbetsorderSearchVirkeskopComponent,
        ArbetsorderSearchMatningComponent,
        ButtonComponent
    ]
})
export class ArbetsorderSearchComponent implements OnInit, OnDestroy {
    @ViewChild(ArbetsorderSearchArbetsmomentComponent) arbetsmomentPanel: ArbetsorderSearchArbetsmomentComponent;
    @Input() basdata: IPlaneraBasdataResponse;
    @ViewChildren(ArbetsorderSearchBasePanel) filterPanels: QueryList<ArbetsorderSearchBasePanel>;
    @ViewChild(ArbetsorderSearchHeaderFilterComponent) headerPanel: ArbetsorderSearchHeaderFilterComponent;
    @Input() hybridService: ArbetsorderSearchHybridService;
    @Input() searchFilter: IGetArbetsorderRequest;
    sokningar: INamedSokfilter[];
    @ViewChild(TabsComponent) tabsComponent: TabsComponent;
    texts = ArbetsorderSearchTexts.texts;
    triggerSparaFilter: IGetArbetsorderRequest;

    private readonly $unsubscribe: Subject<void> = new Subject();
    private readonly emitters: Subscription[] = [];
    private readonly minaFilterService = inject(ArbetsorderSearchMinaFilterService);
    private readonly modalService = inject(RaskModalService);

    confirmDeleteSparadSokning(tab: ITab) {
        this.modalService.confirm(new ModalSettings(this.texts.taBortSokning, `${this.texts.bekraftaBorttagAv} "${tab.label}"`))
            .pipe(take(1))
            .subscribe((ok) => {
                if (ok === ConfirmDialogResponse.save && this.sokningar.find(x => x.namn === tab.label)) {
                    this.deleteSparadSokning(tab.label);
                }
            });
    }

    ngOnDestroy(): void {
        this.emitters.forEach(emitter => emitter.unsubscribe());
        this.emitters.splice(0);
        this.$unsubscribe.next();
        this.$unsubscribe.complete();
    }

    ngOnInit() {
        this.emitters.push(this.hybridService.filtersUpdated
            .subscribe(x => {
                if (this.filterPanels) {
                    this.filterPanels.forEach((panel) => {
                        panel.forceUpdateFilterChange();
                    });
                    this.headerPanel.forceUpdateFilterChange();
                }
            }));

        this.minaFilterService.minaSparadeFilter
            .pipe(takeUntil(this.$unsubscribe))
            .subscribe(x => {
                this.sokningar = Object.assign([], x);
            });

        this.emitters.push(this.hybridService.clearMinaMaskinlag
            .subscribe(() => {
                this.arbetsmomentPanel.forceUpdateFilterChange();
            }));
    }

    onFilterPaneVisibilityChanged(panelEvent: ArbetsorderSearchBasePanel) {
        this.filterPanels.forEach((panel) => panel.hide());
        panelEvent.show();
    }

    performClearFilter() {
        this.deselectMinaFilterTabs();
        this.hybridService.triggerClearFilters();
    }

    performSearchArbetsordrar() {
        this.hybridService.triggerSearchArbetsordrar(this.searchFilter);
    }

    saveSearchParams() {
        // klona till nytt objekt, för att trigga onchanges...
        this.triggerSparaFilter = Object.assign({}, this.searchFilter);
    }

    setSokning(sparadSokningNamn: INamedSokfilter) {
        if (sparadSokningNamn) {
            const kopia = cloneDeep(sparadSokningNamn);
            this.hybridService.triggerSetFilter(kopia);
        }
    }

    private deleteSparadSokning(namn: string) {
        this.minaFilterService.deleteSearchFilterByNamn(namn);
    }

    private deselectMinaFilterTabs() {
        if (this.tabsComponent && this.tabsComponent.activeTab) {
            this.tabsComponent.activeTab = null;
        }
    }
}
